import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { adminGetIntelUser } from '../../../api/Api';
import PageContainer from '../../../shared/PageContainer';
import CyberpunkTitle from '../../../shared/CyberpunkTitle';
import { useColors } from '../../../theme/ColorModeContext';
import type { IntelUserDetail } from './types';
import { CHANNEL_LABELS, LEARNING_LABELS, fmtDate, fmtDateTime, fmtDaysAgo, fmtHours, fmtMinutes, fmtNumber } from './format';
import { MONO, RecencyChip, Section, SmallChip, StageChip, Tile, TileGrid, TrackingNote, TwoColumns, useTableSx } from './widgets';
import { useChartTheme } from './chartTheme';
import ActivityCalendar from '../../../shared/ActivityCalendar';

interface Props {
    token: string;
}

const Progress = ({ label, done, total }: { label: string; done: number; total: number }) => {
    const colors = useColors();
    const chart = useChartTheme();
    return (
        <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.74rem', color: colors.text.secondary }}>{label}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: colors.text.secondary }}>{done} / {total}</Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: '4px', backgroundColor: colors.border.subtle, overflow: 'hidden', mt: '3px' }}>
                <Box sx={{ width: total ? `${(done / total) * 100}%` : 0, height: '100%', borderRadius: '4px', backgroundColor: chart.series[0] }} />
            </Box>
        </Box>
    );
};

const IntelUser = ({ token }: Props) => {
    const { id } = useParams();
    const colors = useColors();
    const table = useTableSx();
    const [data, setData] = useState<IntelUserDetail | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        adminGetIntelUser(token, id).then((result) => {
            if (cancelled) return;
            setData(result);
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [token, id]);

    const statusColor: Record<string, string> = {
        solved: colors.accent.green,
        completed: colors.accent.green,
        stuck: colors.accent.red,
        abandoned: colors.accent.orange,
        in_progress: colors.text.secondary,
        opened: colors.text.muted,
    };
    const insightIcon = {
        warning: <WarningAmberIcon sx={{ color: colors.accent.orange, fontSize: '1.1rem' }} />,
        opportunity: <TrendingUpIcon sx={{ color: colors.accent.green, fontSize: '1.1rem' }} />,
        info: <InfoOutlinedIcon sx={{ color: colors.text.secondary, fontSize: '1.1rem' }} />,
    };

    if (loading) {
        return <PageContainer><Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress sx={{ color: colors.accent.cyan }} /></Box></PageContainer>;
    }
    if (!data) {
        return (
            <PageContainer>
                <CyberpunkTitle segments={[{ label: 'User Intel', to: '/admin/intel?tab=users' }, { label: 'Not found' }]} />
                <Typography sx={{ color: colors.text.muted }}>No such user, or the intel could not be loaded.</Typography>
            </PageContainer>
        );
    }

    const s = data.summary;
    const source = [
        ['Channel', CHANNEL_LABELS[s.signup_channel] || s.signup_channel],
        ['Referring site', s.signup_referrer_host],
        ['UTM source / medium', [s.signup_utm_source, s.signup_utm_medium].filter(Boolean).join(' / ') || null],
        ['UTM campaign', s.signup_utm_campaign],
        ['Landing page', s.signup_landing_path],
        ['Product updates', s.marketing_status],
        ['Email', s.email],
    ];

    return (
        <PageContainer>
            <CyberpunkTitle segments={[{ label: 'User Intel', to: '/admin/intel?tab=users' }, { label: s.username }]} mb="16px" />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', mb: 2 }}>
                <StageChip stage={s.lifecycle_stage} />
                <RecencyChip recency={s.recency} />
                {s.admin && <SmallChip label="Admin" color={colors.accent.purple} />}
                {!s.confirmed && <SmallChip label="Email unconfirmed" color={colors.accent.orange} />}
                <Typography component={RouterLink} to={`/profile/${s.username}`} sx={{ fontFamily: MONO, fontSize: '0.74rem', color: colors.accent.cyan, ml: 1 }}>
                    Public profile →
                </Typography>
            </Box>

            <TrackingNote since={data.tracking_since} />

            {data.insights.length > 0 && (
                <Section title="What stands out">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {data.insights.map((insight, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                {insightIcon[insight.level]}
                                <Typography sx={{ fontSize: '0.84rem', color: colors.text.primary }}>{insight.text}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Section>
            )}

            <TileGrid min={150}>
                <Tile label="Joined" value={fmtDate(s.created_at)} hint={s.confirmed ? `confirmed after ${fmtHours(s.hours_to_confirm)}` : 'not confirmed'} />
                <Tile label="First learning" value={fmtHours(s.hours_to_first_action)} hint={s.first_action ? `after signup · ${LEARNING_LABELS[s.first_action]}` : 'nothing yet'} />
                <Tile label="First solve" value={fmtHours(s.hours_to_first_solve)} hint={s.first_solve_at ? 'after signup' : 'no solve yet'} />
                <Tile label="Last active" value={fmtDaysAgo(s.days_since_active)} hint={fmtDateTime(s.last_active_at)} />
                <Tile label="Active days" value={fmtNumber(s.active_days)} hint={`${s.active_days_30} in the last 30 days`} />
                <Tile label="Streak" value={`${s.current_streak}d`} hint={`longest ${s.longest_streak}d`} />
                <Tile label="Logins" value={fmtNumber(s.logins)} hint={`${fmtNumber(s.visits)} session refreshes`} />
                <Tile label="Solves" value={fmtNumber(s.challenge_solves + s.stage_solves)} hint={`${s.challenge_solves} challenges · ${s.stage_solves} theory · ${s.points} pts`} />
            </TileGrid>

            <Section title="Activity" caption="Darker means more happened that day: challenge runs, answers and solves, plus a mark for being signed in.">
                <ActivityCalendar activity={data.activity} />
            </Section>

            <TwoColumns>
                <Section title="Signup source">
                    {source.map(([label, value]) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: '6px', borderBottom: `1px solid ${colors.border.subtle}` }}>
                            <Typography sx={{ fontSize: '0.78rem', color: colors.text.secondary }}>{label}</Typography>
                            <Typography sx={{ fontFamily: MONO, fontSize: '0.78rem', color: colors.text.primary, textAlign: 'right', wordBreak: 'break-all' }}>{value || '-'}</Typography>
                        </Box>
                    ))}
                </Section>
                <Section title="Track progress" caption="Active content only.">
                    {data.tracks.map((track) => (
                        <Box key={track.track} sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.text.primary, mb: 0.5 }}>{track.label}</Typography>
                            {track.theory_total > 0 && <Progress label="Theory stages" done={track.theory_solved} total={track.theory_total} />}
                            {track.challenges_total > 0 && <Progress label="Challenges" done={track.challenges_solved} total={track.challenges_total} />}
                        </Box>
                    ))}
                </Section>
            </TwoColumns>

            <Section title="Challenges" caption="Every challenge this user opened or solved. Stuck: 3+ failed test runs and no try for 3+ days. Abandoned: untouched for 7+ days.">
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={table.head}>
                                <TableCell>Challenge</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>First opened</TableCell>
                                <TableCell align="right">Compiles</TableCell>
                                <TableCell align="right">Tests (failed)</TableCell>
                                <TableCell align="right">Tests to solve</TableCell>
                                <TableCell align="right">Time to solve</TableCell>
                                <TableCell>Last activity</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.challenges.map((row) => (
                                <TableRow key={row.challenge_id} sx={table.row}>
                                    <TableCell>
                                        {row.name}
                                        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: colors.text.muted }}>{row.track} · {row.category} · diff {row.difficulty}</Typography>
                                    </TableCell>
                                    <TableCell><SmallChip label={row.status.replace('_', ' ')} color={statusColor[row.status]} /></TableCell>
                                    <TableCell sx={table.mono}>{fmtDateTime(row.first_opened_at)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.compile_count ?? '-'}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.test_count ?? '-'}{row.failed_test_count != null && ` (${row.failed_test_count})`}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.tests_to_solve ?? '-'}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtHours(row.hours_to_solve)}</TableCell>
                                    <TableCell sx={table.mono}>{fmtDateTime(row.last_activity_at)}</TableCell>
                                </TableRow>
                            ))}
                            {!data.challenges.length && <TableRow sx={table.row}><TableCell colSpan={8}>No challenges touched.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            <Section title="Theory stages">
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={table.head}>
                                <TableCell>Stage</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>First opened</TableCell>
                                <TableCell align="right">Answers (wrong)</TableCell>
                                <TableCell align="right">Time to complete</TableCell>
                                <TableCell>Completed</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.stages.map((row) => (
                                <TableRow key={row.stage_id} sx={table.row}>
                                    <TableCell>
                                        {row.order_index}. {row.title}
                                        <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: colors.text.muted }}>{row.track} · {row.category}</Typography>
                                    </TableCell>
                                    <TableCell><SmallChip label={row.status.replace('_', ' ')} color={statusColor[row.status]} /></TableCell>
                                    <TableCell sx={table.mono}>{fmtDateTime(row.first_viewed_at)}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{row.answers_count ?? '-'}{row.wrong_answers_count != null && ` (${row.wrong_answers_count})`}</TableCell>
                                    <TableCell align="right" sx={table.mono}>{fmtMinutes(row.minutes_to_complete)}</TableCell>
                                    <TableCell sx={table.mono}>{fmtDateTime(row.solved_at)}</TableCell>
                                </TableRow>
                            ))}
                            {!data.stages.length && <TableRow sx={table.row}><TableCell colSpan={6}>No theory stages touched.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Section>

            {data.quiz_attempts.length > 0 && (
                <Section title="Tech quizzes (signed in)">
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={table.head}>
                                    <TableCell>Quiz</TableCell>
                                    <TableCell>Started</TableCell>
                                    <TableCell align="right">Score</TableCell>
                                    <TableCell align="right">Time</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.quiz_attempts.map((row) => (
                                    <TableRow key={row.started_at + row.quiz_slug} sx={table.row}>
                                        <TableCell>{row.title}</TableCell>
                                        <TableCell sx={table.mono}>{fmtDateTime(row.started_at)}</TableCell>
                                        <TableCell align="right" sx={table.mono}>{row.completed ? `${row.score} / ${row.total_questions}` : 'not finished'}</TableCell>
                                        <TableCell align="right" sx={table.mono}>{row.duration_seconds != null ? fmtMinutes(row.duration_seconds / 60) : '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Section>
            )}

            <Section title="Timeline" caption="Newest first. Sign-ins are grouped per day.">
                <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
                    {data.timeline.map((event, index) => (
                        <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '170px 1fr' }, gap: { xs: 0, sm: 2 }, py: '7px', borderBottom: `1px solid ${colors.border.subtle}` }}>
                            <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: colors.text.muted }}>{fmtDateTime(event.at)}</Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: event.kind === 'solve' ? colors.accent.green : colors.text.primary }}>{event.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Section>
        </PageContainer>
    );
};

export default IntelUser;
